import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Save } from "lucide-react";
import { toast } from "sonner";
import { NeuButton } from "../../components/neu/Neu";
import { DirtyGuardDialog } from "../../context/DirtyGuardDialog";
import { createDepartment, updateDepartment } from "../../api/departments";
import { listUsers, listBranches } from "../../api/directory";
import { DeptIconPicker } from "./DeptIconPicker";
import { DepartmentFormFields } from "./DepartmentFormFields";
import { DepartmentMembers } from "./DepartmentMembers";
import { DeptPreview } from "./DeptPreview";
import { DEPT_COLORS } from "./deptIcons";
import { localName } from "../../lib/localName";

const EMPTY = { name_ar: "", name_en: "", code: "", description: "",
                description_en: "", icon_key: "building",
                color: DEPT_COLORS[0], manager_id: "", branch_id: "" };

export const DepartmentForm = ({ open, initial, prefill, departments, onCancel, onSaved }) => {
  const { t, i18n } = useTranslation();
  const isAr = (i18n.language || "ar").startsWith("ar");
  const [form, setForm] = useState(EMPTY);
  const [managers, setManagers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const snapshot = useRef("");

  useEffect(() => {
    if (!open) return;
    const init = { ...EMPTY, ...(prefill || {}), ...(initial || {}) };
    setForm(init);
    snapshot.current = JSON.stringify(init);
    (async () => { try { setManagers(await listUsers(null, "active")); setBranches(await listBranches()); } catch {/* ignore */} })();
    const onKey = (e) => { if (e.key === "Escape") onCancel?.(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, initial, prefill, onCancel]);

  if (!open) return null;
  const dirty = JSON.stringify(form) !== snapshot.current;
  const guardClose = () => (dirty ? setConfirm(true) : onCancel?.());

  const submit = async (force = false) => {
    if (!form.name_ar.trim() || !form.name_en.trim()) return toast.error(t("departments.errors.namesRequired"));
    setBusy(true);
    try {
      const body = { name_ar: form.name_ar.trim(), name_en: form.name_en.trim(),
                     code: form.code ? form.code.trim().toUpperCase() : null,
                     description: form.description?.trim() || null, description_en: form.description_en?.trim() || null,
                     icon_key: form.icon_key, color: form.color, manager_id: form.manager_id || null, branch_id: form.branch_id || null };
      const saved = initial?.id
        ? await updateDepartment(initial.id, body, force)
        : await createDepartment(body, force);
      toast.success(t(initial ? "departments.updated" : "departments.created"));
      onSaved?.(saved);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.code === "manager_taken" && !force && window.confirm(t("departments.errors.manager_taken", { name: localName(d) }))) { setBusy(false); return submit(true); }
      toast.error(t(d?.code ? `departments.errors.${d.code}` : "departments.errors.saveFailed"));
    } finally { setBusy(false); }
  };

  return (
    <div dir="rtl" data-testid="dept-form-modal" className="fixed inset-0 z-50 flex h-[100dvh] w-full flex-col bg-background animate-in fade-in">
      <header className="flex items-center justify-between border-b border-border/40 bg-background/95 px-4 py-3 shadow-neu-sm sm:px-8">
        <p className="text-lg font-bold text-primary" data-testid="dept-form-title">{t(initial ? "departments.editTitle" : "departments.addTitle")}</p>
        <button onClick={guardClose} data-testid="dept-form-close" className="rounded-full p-2 text-muted-foreground shadow-neu-sm hover:text-destructive"><X className="h-4 w-4" /></button>
      </header>
      <main className="flex-1 overflow-y-auto overscroll-contain px-4 py-6 sm:px-8 lg:px-12">
        <div className="mx-auto grid w-full max-w-7xl gap-6 lg:grid-cols-[1.3fr_1fr_320px]">
          <div className="grid gap-4">
            <DepartmentFormFields form={form} onChange={setForm} managers={managers} branches={branches} departments={departments} currentId={initial?.id} isEdit={Boolean(initial?.id)} />
            <DepartmentMembers
              deptId={initial?.id}
              branchName={branches.find((b) => b.id === form.branch_id)?.name_ar || branches.find((b) => b.id === form.branch_id)?.name}
            />
          </div>
          <DeptIconPicker iconKey={form.icon_key} color={form.color} onIcon={(k) => setForm({ ...form, icon_key: k })} onColor={(c) => setForm({ ...form, color: c })} />
          <DeptPreview form={form} managers={managers} initial={initial} />
        </div>
      </main>
      <footer className="flex items-center justify-end gap-2 border-t border-border/40 bg-background/95 px-4 py-3 shadow-neu-sm sm:px-8">
        <NeuButton onClick={guardClose} data-testid="dept-form-cancel">{t("app.cancel")}</NeuButton>
        <NeuButton variant="primary" onClick={() => submit(false)} disabled={busy} data-testid="dept-form-save" className="flex items-center gap-2"><Save className="h-4 w-4" /> {t("app.save")}</NeuButton>
      </footer>
      <DirtyGuardDialog open={confirm} busy={busy}
        onSave={() => submit(false)}
        onDiscard={() => { setConfirm(false); onCancel?.(); }}
        onCancel={() => setConfirm(false)} />
    </div>
  );
};
